import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { clientAccessService, ClientAccessConfig } from '../services/clientAccessService';
import { clientService } from '../services/clientService';
import { useHeader } from '../contexts/HeaderContext';
import LoadingSpinner from '../components/LoadingSpinner';

const Settings: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);
  const { setTitle } = useHeader();

  const { data: client, isLoading: clientLoading } = useQuery(
    ['client', clientId],
    () => clientService.getClient(parseInt(clientId!)),
    { enabled: !!clientId }
  );

  React.useEffect(() => {
    if (client) {
      setTitle(`${client.name} Settings`);
    } else {
      setTitle('Settings');
    }
  }, [client, setTitle]);

  const { data: config, isLoading: configLoading } = useQuery(
    ['client-access-config', clientId],
    () => clientAccessService.getClientAccessConfig(parseInt(clientId!)),
    { enabled: !!clientId }
  );

  const updateMutation = useMutation(
    (data: Partial<ClientAccessConfig>) =>
      clientAccessService.updateClientAccessConfig(parseInt(clientId!), data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-access-config', clientId]);
        setSaving(false);
      },
      onError: () => {
        setSaving(false);
      }
    }
  );

  const handleSave = async () => {
    if (!config) return;

    setSaving(true);
    await updateMutation.mutateAsync({
      canViewOwnQuestionnaire: config.canViewOwnQuestionnaire,
      canViewReports: config.canViewReports,
      canViewDashboard: config.canViewDashboard
    });
  };

  const handleToggle = (field: keyof ClientAccessConfig) => {
    if (!config) return;

    const updatedConfig = {
      ...config,
      [field]: !config[field]
    };

    // Optimistically update the UI
    queryClient.setQueryData(['client-access-config', clientId], updatedConfig);
  };

  if (clientLoading || configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Client access configuration not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Client Access Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configure access permissions for {client?.name || 'this client'}
        </p>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Access Permissions</h3>
          <p className="mt-1 text-sm text-gray-500">
            Control what this client can access in the system.
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Can View Own Questionnaire */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900">
                View Own Questionnaire
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Allow client to view their own questionnaire and assessments
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('canViewOwnQuestionnaire')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${config.canViewOwnQuestionnaire ? 'bg-blue-600' : 'bg-gray-200'
                }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${config.canViewOwnQuestionnaire ? 'translate-x-5' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>

          {/* Can View Reports */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900">
                View Reports
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Allow client to view and download assessment reports
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('canViewReports')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${config.canViewReports ? 'bg-blue-600' : 'bg-gray-200'
                }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${config.canViewReports ? 'translate-x-5' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>

          {/* Can View Dashboard */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900">
                View Dashboard
              </label>
              <p className="text-sm text-gray-500 mt-1">
                Allow client to view the dashboard with overview statistics
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleToggle('canViewDashboard')}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${config.canViewDashboard ? 'bg-blue-600' : 'bg-gray-200'
                }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${config.canViewDashboard ? 'translate-x-5' : 'translate-x-0'
                  }`}
              />
            </button>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> Question-level permissions can be configured inline within each assessment questionnaire.
        </p>
      </div>
    </div>
  );
};

export default Settings;

